import React from 'react';
import { Platform } from 'react-native';
import { Button, ButtonIcon } from '@gluestack-ui/themed';
import { Paperclip } from 'lucide-react-native';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { sanitizeFilename } from '@/common/utils/fileUtils';
import log from '@/common/utils/logger';

type NativeUploadFile = {
  uri: string;
  name: string;
  type: string;
};

interface AttachmentUploadButtonProps {
  onUploadAttachment: (file: Blob | File | NativeUploadFile) => Promise<void>;
  readonly?: boolean;
  onPress?: () => void;
}

const AttachmentUploadButton: React.FC<AttachmentUploadButtonProps> = ({
  onUploadAttachment,
  readonly = false,
  onPress,
}) => {
  const prepareFileUpload = async (
    pickerResult: ImagePicker.ImagePickerResult | DocumentPicker.DocumentPickerResult,
  ) => {
    if (Platform.OS === 'web') {
      const { file: webFile } = pickerResult.assets[0] as { file: File };
      const targetFilename = sanitizeFilename(webFile.name);
      return new File([webFile], targetFilename, { type: webFile.type });
    }

    const asset = pickerResult.assets[0];
    const { uri, name: assetName, mimeType: assetType } = asset as { uri: string; name: string; mimeType: string };
    const originalFilename = assetName || uri.substring(uri.lastIndexOf('/') + 1);
    const targetFilename = sanitizeFilename(originalFilename);

    const fileObject = {
      uri,
      type: assetType || 'application/octet-stream',
      name: targetFilename,
    };

    log.debug('[Debug] Prepared file object:', fileObject);
    return fileObject;
  };

  const pickFile = async (launchPicker: () => Promise<ImagePicker.ImagePickerResult | DocumentPicker.DocumentPickerResult>) => {
    const pickerResult = await launchPicker();

    if (!pickerResult.canceled) {
      try {
        const fileObject = await prepareFileUpload(pickerResult);
        await onUploadAttachment(fileObject);
      } catch (error) {
        log.error('Error trying to upload file:', error);
      }
    }
  };

  const pickDocument = () => pickFile(() => DocumentPicker.getDocumentAsync({
    type: '*/*',
    copyToCacheDirectory: true,
  }));

  return (
    <Button
      size="xs"
      variant="link"
      action="secondary"
      isDisabled={readonly}
      onPress={onPress || pickDocument}
    >
      <ButtonIcon as={Paperclip} color="$textSubtle" size="md" ml="$4" />
    </Button>
  );
};

export default AttachmentUploadButton;
